import { useState, useRef } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { Editor } from '@/components/Editor';
import { ImageCropper } from '@/components/ImageCropper';

/**
 * Issue data for selection.
 */
interface Issue {
    id: number;
    issue_number: number;
    title: string | null;
    display_title: string;
    article_count: number;
    is_published: boolean;
}

/**
 * Props for the Create page.
 */
interface Props {
    nextIssueNumber: number;
    issues: Issue[];
}

/**
 * GM Newspaper Create page - form to create a new newspaper article.
 */
export default function Create({ nextIssueNumber, issues }: Props) {
    // Form fields
    const [title, setTitle] = useState('');
    const [byline, setByline] = useState('');
    const [content, setContent] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [coverImage, setCoverImage] = useState<File | null>(null);
    const [coverImagePreview, setCoverImagePreview] = useState<string | null>(null);
    const [isPublished, setIsPublished] = useState(false);

    // Issue selection state
    const [issueMode, setIssueMode] = useState<'existing' | 'new'>('new');
    const [selectedIssueId, setSelectedIssueId] = useState<number | null>(null);
    const [newIssueTitle, setNewIssueTitle] = useState('');

    // Image cropper state
    const [imageToCrop, setImageToCrop] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Form state
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Handle file selection - opens the cropper modal.
     */
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Read the file and open the cropper
        const reader = new FileReader();
        reader.onload = () => {
            setImageToCrop(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    /**
     * Handle crop completion - receives the cropped image blob.
     */
    const handleCropComplete = (croppedBlob: Blob) => {
        // Convert blob to File for FormData
        const croppedFile = new File([croppedBlob], 'cover.jpg', { type: 'image/jpeg' });
        setCoverImage(croppedFile);

        // Create preview URL
        setCoverImagePreview(URL.createObjectURL(croppedBlob));

        // Close cropper
        setImageToCrop(null);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Handle crop cancellation.
     */
    const handleCropCancel = () => {
        setImageToCrop(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Remove the selected cover image.
     */
    const handleRemoveCoverImage = () => {
        setCoverImage(null);
        setCoverImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Handle form submission.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        // Use FormData for file upload
        const formData = new FormData();
        formData.append('title', title);
        formData.append('content', content);
        formData.append('is_published', isPublished ? '1' : '0');
        if (byline) formData.append('byline', byline);
        if (excerpt) formData.append('excerpt', excerpt);
        if (coverImage) formData.append('cover_image', coverImage);

        // Issue selection
        if (issueMode === 'new') {
            formData.append('create_new_issue', '1');
            if (newIssueTitle) formData.append('new_issue_title', newIssueTitle);
        } else if (selectedIssueId) {
            formData.append('issue_id', selectedIssueId.toString());
        }

        router.post('/gm/newspaper', formData, {
            forceFormData: true,
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => setIsSubmitting(false),
        });
    };

    return (
        <GmLayout title="Create Article" activePath="/gm/newspaper">
            <div className="max-w-4xl">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-white">Create Article</h1>
                        <p className="text-sm text-gray-400">Add a new article to an issue</p>
                    </div>
                    <Link href="/gm/newspaper" className="text-gray-400 hover:text-white transition-colors">
                        Back to Newspaper
                    </Link>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Issue Selection Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Issue Assignment</h2>

                        <div className="flex gap-4 mb-4">
                            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                <input
                                    type="radio"
                                    name="issueMode"
                                    checked={issueMode === 'new'}
                                    onChange={() => setIssueMode('new')}
                                    className="text-purple-600"
                                />
                                Create new issue
                            </label>
                            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                                <input
                                    type="radio"
                                    name="issueMode"
                                    checked={issueMode === 'existing'}
                                    onChange={() => setIssueMode('existing')}
                                    className="text-purple-600"
                                    disabled={issues.length === 0}
                                />
                                Add to existing issue
                            </label>
                        </div>

                        {issueMode === 'new' ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Issue Title (optional)
                                </label>
                                <input
                                    type="text"
                                    value={newIssueTitle}
                                    onChange={(e) => setNewIssueTitle(e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    placeholder={`Leave blank for "Issue #${nextIssueNumber}"`}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    This will create Issue #{nextIssueNumber}
                                </p>
                            </div>
                        ) : (
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">
                                    Select Issue
                                </label>
                                <select
                                    value={selectedIssueId ?? ''}
                                    onChange={(e) => setSelectedIssueId(e.target.value ? Number(e.target.value) : null)}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                >
                                    <option value="">Select an issue...</option>
                                    {issues.map((issue) => (
                                        <option key={issue.id} value={issue.id}>
                                            {issue.display_title} ({issue.article_count} article{issue.article_count !== 1 ? 's' : ''})
                                            {issue.is_published ? ' - Published' : ' - Draft'}
                                        </option>
                                    ))}
                                </select>
                                {errors.issue_id && <p className="mt-1 text-sm text-red-400">{errors.issue_id}</p>}
                            </div>
                        )}
                    </div>

                    {/* Basic Info Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white mb-4">Article Information</h2>

                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Headline <span className="text-red-400">*</span>
                            </label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="Breaking News from the Faewild!"
                                required
                            />
                            {errors.title && <p className="mt-1 text-sm text-red-400">{errors.title}</p>}
                        </div>

                        {/* Byline */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Byline (Author Name)
                            </label>
                            <input
                                type="text"
                                value={byline}
                                onChange={(e) => setByline(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="Leave blank to use your name"
                            />
                            {errors.byline && <p className="mt-1 text-sm text-red-400">{errors.byline}</p>}
                        </div>

                        {/* Cover Image */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Cover Image
                            </label>
                            <p className="text-xs text-gray-500 mb-2">
                                Image will be cropped to 864×384 pixels (2.25:1 aspect ratio)
                            </p>
                            {/* Preview of cropped image */}
                            {coverImagePreview && (
                                <div className="mb-3">
                                    <img
                                        src={coverImagePreview}
                                        alt="Cover preview"
                                        className="w-full max-w-md h-auto rounded border border-slate-600"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveCoverImage}
                                        className="mt-2 text-xs text-red-400 hover:text-red-300"
                                    >
                                        Remove cover image
                                    </button>
                                </div>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:bg-purple-600 file:text-white file:cursor-pointer"
                            />
                            {errors.cover_image && <p className="mt-1 text-sm text-red-400">{errors.cover_image}</p>}
                        </div>

                        {/* Content - Markdown Editor */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Article Content <span className="text-red-400">*</span>
                            </label>
                            <p className="text-xs text-gray-500 mb-2">
                                Use Markdown to format your article.
                            </p>
                            <div data-color-mode="dark">
                                <Editor
                                    value={content}
                                    onChange={(value) => setContent(value)}
                                    placeholder="Write your article here..."
                                    height={400}
                                />
                            </div>
                            {errors.content && <p className="mt-1 text-sm text-red-400">{errors.content}</p>}
                        </div>

                        {/* Excerpt */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Excerpt (Preview Text)
                            </label>
                            <textarea
                                value={excerpt}
                                onChange={(e) => setExcerpt(e.target.value)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                placeholder="Leave blank to auto-generate from first paragraph"
                                rows={3}
                            />
                            {errors.excerpt && <p className="mt-1 text-sm text-red-400">{errors.excerpt}</p>}
                        </div>

                        {/* Published Status */}
                        <div className="flex items-center">
                            <label className="flex items-center gap-2 text-sm text-gray-300">
                                <input
                                    type="checkbox"
                                    checked={isPublished}
                                    onChange={(e) => setIsPublished(e.target.checked)}
                                    className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                />
                                Publish immediately (visible to all users)
                            </label>
                        </div>
                        <p className="text-xs text-gray-500">
                            Draft articles are only visible to GMs. You can publish later from the edit page.
                        </p>
                    </div>

                    {/* Submit Buttons */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? 'Creating...' : 'Create Article'}
                        </button>
                        <Link
                            href="/gm/newspaper"
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>

            {/* Image Cropper Modal */}
            {imageToCrop && (
                <ImageCropper
                    imageSrc={imageToCrop}
                    onCropComplete={handleCropComplete}
                    onCancel={handleCropCancel}
                    targetWidth={864}
                    targetHeight={384}
                />
            )}
        </GmLayout>
    );
}

